import { z } from 'zod';
import { isoDate, paginacao, taxa } from './primitives.js';

/**
 * Origem do lucro desejado/minimo que a API entregou ao motor (DP6): `TABELA` (vigencia da tabela define),
 * `EMPRESA` (tabela nula, vale o padrao da empresa) ou `AUSENTE` (nenhum dos dois: minimo 0).
 */
export type OrigemLucro = 'TABELA' | 'EMPRESA' | 'AUSENTE';
export const ROTULO_ORIGEM_LUCRO: Record<OrigemLucro, string> = {
  TABELA: 'da tabela',
  EMPRESA: 'padrão da empresa',
  AUSENTE: 'sem padrão (mínimo 0)',
};

/** Lucro resolvido gravado no snapshot (`refs.lucro`). */
export interface LucroResolvido {
  desejado: { valor: string | null; origem: OrigemLucro; parametroId?: string | null };
  minimo: { valor: string; origem: OrigemLucro; parametroId?: string | null };
}

export const despesaKind = z.enum(['TRIBUTO', 'COMISSAO', 'OUTRA']);
/** Sessao 15 (6b, P2): a linha de despesa e este tributo (a taxa cadastrada e a da legislacao; o motor aplica o fator do ano). `OUTRO` = tributo que nao transita (IRPJ, CSLL, DAS): revisado, sem fator. */
export const despesaTaxCode = z.enum(['ICMS', 'ISS', 'PIS', 'COFINS', 'OUTRO']);
/** P7: base da despesa: preco sem tributos (padrao) ou total da NF. */
export const despesaBase = z.enum(['PRECO_SEM_TRIBUTOS', 'TOTAL_NF']);

export const criarTabelaPrecoSchema = z.object({
  codigo: z.string().trim().min(1).max(40),
  nome: z.string().trim().min(1).max(200),
  isDefault: z.boolean().default(false),
  ativa: z.boolean().default(true),
  /** P3: o preco praticado digitado inclui CBS/IBS (tabela de venda a nao contribuintes). */
  precoIncluiCbsIbs: z.boolean().default(false),
});
export type CriarTabelaPrecoDto = z.infer<typeof criarTabelaPrecoSchema>;

export const atualizarTabelaPrecoSchema = z
  .object({
    nome: z.string().trim().min(1).max(200),
    isDefault: z.boolean(),
    ativa: z.boolean(),
    precoIncluiCbsIbs: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarTabelaPrecoDto = z.infer<typeof atualizarTabelaPrecoSchema>;

export const listarTabelasPrecoSchema = paginacao.extend({
  ativa: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type ListarTabelasPrecoQuery = z.infer<typeof listarTabelasPrecoSchema>;

/** Nova vigencia: fecha a vigente (validTo = validFrom) e insere esta. Valores nunca sao editados. */
export const novaVigenciaSchema = z.object({
  validFrom: isoDate,
  /** Fracao da TABELA; ausente/null = vale o padrao da empresa (DP6). */
  desiredProfitRate: taxa.nullish(),
  minimumProfitRate: taxa.nullish(),
  motivo: z.string().trim().min(1).max(300).optional(),
  despesas: z
    .array(
      z.object({
        description: z.string().trim().min(1).max(200),
        kind: despesaKind,
        rate: taxa,
        taxCode: despesaTaxCode.nullish(),
        base: despesaBase.default('PRECO_SEM_TRIBUTOS'),
      }),
    )
    .max(50)
    .default([]),
});
export type NovaVigenciaDto = z.infer<typeof novaVigenciaSchema>;

export interface DespesaResponse {
  ordem: number;
  description: string;
  kind: z.infer<typeof despesaKind>;
  rate: string;
  taxCode: z.infer<typeof despesaTaxCode> | null;
  base: z.infer<typeof despesaBase>;
}

export interface VigenciaResponse {
  id: string;
  validFrom: string;
  validTo: string | null;
  /** Valor da tabela; null = segue o padrao da empresa. */
  desiredProfitRate: string | null;
  minimumProfitRate: string | null;
  /** Valor resolvido na data de hoje (tabela > empresa) e a origem (DP6). `lucroDesejado` nulo = nenhum dos dois define. */
  lucroDesejado: string | null;
  lucroDesejadoOrigem: OrigemLucro;
  lucroMinimo: string;
  lucroMinimoOrigem: OrigemLucro;
  /** Estrutura de despesa da tabela: so com custo.ver (a API omite para VENDEDOR e LEITURA). */
  despesas?: DespesaResponse[];
  /** Soma dos % das despesas da vigencia (fracao), calculada na API; so com custo.ver, como `despesas`. */
  totalDespesas?: string;
}

export interface TabelaPrecoResponse {
  id: string;
  codigo: string;
  nome: string;
  isDefault: boolean;
  ativa: boolean;
  /** P3: preco praticado inclui CBS/IBS. */
  precoIncluiCbsIbs: boolean;
  vigencias?: VigenciaResponse[];
  createdAt: string;
  updatedAt: string;
}
